import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
} from "@mui/material";
import { useNotify, useDataProvider, useRefresh } from "react-admin";
import { baseUrl } from "../../customDataProvider";
import { useTenant } from "../../contexts/TenantContext";
import * as XLSX from "xlsx";

interface EquipmentRow {
  "Name": string;
  "Type": string;
  "Active"?: boolean;
  "VIN"?: string;
  "License Plate"?: string;
  "Jurisdiction"?: string;
  "Manufactured Year"?: number;
  "Make"?: string;
  "Model"?: string;
  "Mode"?: string;
  "Transmission"?: string;
  "Ownership Type"?: string;
  "Acquire Date"?: string;
  "Dispose Date"?: string;
  "List Price New"?: number;
  "Actual Cash Value"?: number;
  "Max Hauled Kg"?: number;
  "Trailer Axle Count"?: number;
  "Trailer Length"?: number;
  "Trailer Type"?: string;
  "CVOR"?: string;
  "US DOT Number"?: string;
  "Registration Expiry"?: string;
  "Safety Expiry"?: string;
  "Insurance Expiry"?: string;
  "Notes"?: string;
}

interface BulkEquipmentUploadDialogProps {
  open: boolean;
  onClose: () => void;
}

const equipmentTypes = ["BULLDOZER", "BUS", "CAR", "CEMENT_TRUCK", "CRANE", "DELIVERY_VAN", "DUMP_TRUCK", "EQUIPMENT", "EXCAVATOR", "FLATBED", "FORKLIFT", "GARBAGE_TRUCK", "MOTOR_COACH", "OTHER", "SCHOOL_BUS", "TOW_TRUCK", "TRACTOR", "TRAILER", "TRUCK", "VAN"];
const transmissionTypes = ["AUTOMATIC", "MANUAL"];
const ownershipTypes = ["OWNED", "LEASED"];

export const BulkEquipmentUploadDialog = ({
  open,
  onClose,
}: BulkEquipmentUploadDialogProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [equipmentData, setEquipmentData] = useState<EquipmentRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  
  // Get tenant from context (component should be within TenantProvider)
  const tenantContext = useTenant();
  const currentTenant = tenantContext?.currentTenant || null;

  const steps = ["Upload File", "Review Data", "Confirm"];

  // Function to normalize column names for matching
  const normalizeColumnName = (name: string): string => {
    return name.toLowerCase().replace(/[\s_-]/g, '');
  };

  // Create a mapping of normalized names to expected names
  const columnMapping: { [key: string]: keyof EquipmentRow } = {
    [normalizeColumnName("Name")]: "Name",
    [normalizeColumnName("Equipment Name")]: "Name",
    [normalizeColumnName("Type")]: "Type",
    [normalizeColumnName("Equipment Type")]: "Type",
    [normalizeColumnName("Active")]: "Active",
    [normalizeColumnName("Is Active")]: "Active",
    [normalizeColumnName("VIN")]: "VIN",
    [normalizeColumnName("License Plate")]: "License Plate",
    [normalizeColumnName("LicensePlate")]: "License Plate",
    [normalizeColumnName("license_plate")]: "License Plate",
    [normalizeColumnName("Plate")]: "License Plate",
    [normalizeColumnName("Jurisdiction")]: "Jurisdiction",
    [normalizeColumnName("Manufactured Year")]: "Manufactured Year",
    [normalizeColumnName("ManufacturedYear")]: "Manufactured Year",
    [normalizeColumnName("manufactured_year")]: "Manufactured Year",
    [normalizeColumnName("Year")]: "Manufactured Year",
    [normalizeColumnName("Make")]: "Make",
    [normalizeColumnName("Model")]: "Model",
    [normalizeColumnName("Mode")]: "Mode",
    [normalizeColumnName("Transmission")]: "Transmission",
    [normalizeColumnName("Transmission Type")]: "Transmission",
    [normalizeColumnName("Ownership Type")]: "Ownership Type",
    [normalizeColumnName("OwnershipType")]: "Ownership Type",
    [normalizeColumnName("ownership_type")]: "Ownership Type",
    [normalizeColumnName("Ownership")]: "Ownership Type",
    [normalizeColumnName("Acquire Date")]: "Acquire Date",
    [normalizeColumnName("AcquireDate")]: "Acquire Date",
    [normalizeColumnName("acquire_date")]: "Acquire Date",
    [normalizeColumnName("Purchase Date")]: "Acquire Date",
    [normalizeColumnName("Dispose Date")]: "Dispose Date",
    [normalizeColumnName("DisposeDate")]: "Dispose Date",
    [normalizeColumnName("dispose_date")]: "Dispose Date",
    [normalizeColumnName("List Price New")]: "List Price New",
    [normalizeColumnName("ListPriceNew")]: "List Price New",
    [normalizeColumnName("list_price_new")]: "List Price New",
    [normalizeColumnName("List Price")]: "List Price New",
    [normalizeColumnName("Actual Cash Value")]: "Actual Cash Value",
    [normalizeColumnName("ActualCashValue")]: "Actual Cash Value",
    [normalizeColumnName("actual_cash_value")]: "Actual Cash Value",
    [normalizeColumnName("Cash Value")]: "Actual Cash Value",
    [normalizeColumnName("Max Hauled Kg")]: "Max Hauled Kg",
    [normalizeColumnName("MaxHauledKg")]: "Max Hauled Kg",
    [normalizeColumnName("max_hauled_kg")]: "Max Hauled Kg",
    [normalizeColumnName("Max Hauled")]: "Max Hauled Kg",
    [normalizeColumnName("Trailer Axle Count")]: "Trailer Axle Count",
    [normalizeColumnName("TrailerAxleCount")]: "Trailer Axle Count",
    [normalizeColumnName("trailer_axle_count")]: "Trailer Axle Count",
    [normalizeColumnName("Axle Count")]: "Trailer Axle Count",
    [normalizeColumnName("Trailer Length")]: "Trailer Length",
    [normalizeColumnName("TrailerLength")]: "Trailer Length",
    [normalizeColumnName("trailer_length")]: "Trailer Length",
    [normalizeColumnName("Trailer Type")]: "Trailer Type",
    [normalizeColumnName("TrailerType")]: "Trailer Type",
    [normalizeColumnName("trailer_type")]: "Trailer Type",
    [normalizeColumnName("CVOR")]: "CVOR",
    [normalizeColumnName("US DOT Number")]: "US DOT Number",
    [normalizeColumnName("UsDotNumber")]: "US DOT Number",
    [normalizeColumnName("us_dot_number")]: "US DOT Number",
    [normalizeColumnName("DOT Number")]: "US DOT Number",
    [normalizeColumnName("Registration Expiry")]: "Registration Expiry",
    [normalizeColumnName("RegistrationExpiry")]: "Registration Expiry",
    [normalizeColumnName("registration_expiry")]: "Registration Expiry",
    [normalizeColumnName("Safety Expiry")]: "Safety Expiry",
    [normalizeColumnName("SafetyExpiry")]: "Safety Expiry",
    [normalizeColumnName("safety_expiry")]: "Safety Expiry",
    [normalizeColumnName("Insurance Expiry")]: "Insurance Expiry",
    [normalizeColumnName("InsuranceExpiry")]: "Insurance Expiry",
    [normalizeColumnName("insurance_expiry")]: "Insurance Expiry",
    [normalizeColumnName("Notes")]: "Notes",
    [normalizeColumnName("Comments")]: "Notes",
    [normalizeColumnName("Description")]: "Notes",
  };

  const parseBoolean = (value: any): boolean | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      if (lower === 'true' || lower === 'yes' || lower === '1') return true;
      if (lower === 'false' || lower === 'no' || lower === '0') return false;
    }
    if (typeof value === 'number') return value !== 0;
    return undefined;
  };

  const parseNumber = (value: any): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  };

  const normalizeEquipmentType = (value: any): string | undefined => {
    if (value === undefined || value === null) return undefined;
    const stringInput = String(value).trim();
    if (!stringInput) return undefined;

    // Try exact match first (case-insensitive)
    const upper = stringInput.toUpperCase().replace(/\s+/g, "_");
    if (equipmentTypes.includes(upper)) return upper;

    // Normalize and check common variations
    const cleaned = stringInput.toLowerCase().replace(/[.,;-]/g, " ").replace(/\s+/g, " ").trim();
    
    // Common type mappings
    const typeMap: Record<string, string> = {
      "truck": "TRUCK", "pickup": "TRUCK", "pickup truck": "TRUCK",
      "trailer": "TRAILER", "semi trailer": "TRAILER",
      "car": "CAR", "sedan": "CAR", "suv": "CAR", "vehicle": "CAR",
      "van": "VAN", "cargo van": "VAN", "sprinter": "VAN",
      "tractor": "TRACTOR", "semi": "TRACTOR", "big rig": "TRACTOR",
      "bus": "BUS", "coach": "BUS", "shuttle": "BUS",
      "crane": "CRANE", "forklift": "FORKLIFT", "fork lift": "FORKLIFT",
      "excavator": "EXCAVATOR", "backhoe": "EXCAVATOR", "digger": "EXCAVATOR",
      "bulldozer": "BULLDOZER", "dozer": "BULLDOZER",
      "cement truck": "CEMENT_TRUCK", "concrete truck": "CEMENT_TRUCK",
      "dump truck": "DUMP_TRUCK", "dump": "DUMP_TRUCK",
      "garbage truck": "GARBAGE_TRUCK", "trash truck": "GARBAGE_TRUCK",
      "tow truck": "TOW_TRUCK", "wrecker": "TOW_TRUCK",
      "delivery van": "DELIVERY_VAN", "box truck": "DELIVERY_VAN",
      "flatbed": "FLATBED", "flat bed": "FLATBED",
    };

    return typeMap[cleaned];
  };

  const normalizeTransmissionType = (value: any): string | undefined => {
    if (value === undefined || value === null) return undefined;
    const stringInput = String(value).trim();
    if (!stringInput) return undefined;

    // Try exact match first (case-insensitive)
    const upper = stringInput.toUpperCase().replace(/\s+/g, "_");
    if (transmissionTypes.includes(upper)) return upper;

    const cleaned = stringInput.toLowerCase().replace(/[.,;-]/g, " ").replace(/\s+/g, " ").trim();

    // Map CVT and SEMI_AUTOMATIC to AUTOMATIC (they don't exist in backend enum)
    if (cleaned.includes("auto") || cleaned === "a" || cleaned === "at" || 
        cleaned.includes("cvt") || cleaned.includes("semi") || 
        cleaned.includes("dual clutch") || cleaned === "dct") {
      return "AUTOMATIC";
    }

    if (cleaned.includes("manual") || cleaned === "m" || cleaned === "mt" || 
        cleaned.includes("stick")) {
      return "MANUAL";
    }

    return undefined;
  };

  const normalizeOwnershipType = (value: any): string | undefined => {
    if (value === undefined || value === null) return undefined;
    const stringInput = String(value).trim();
    if (!stringInput) return undefined;

    // Try exact match first (case-insensitive)
    const upper = stringInput.toUpperCase().replace(/\s+/g, "_");
    if (ownershipTypes.includes(upper)) return upper;

    const cleaned = stringInput
      .toLowerCase()
      .replace(/[.,;]/g, "")
      .replace(/-/g, " ")
      .replace(/\s+/g, " ");

    if (cleaned.includes("capital lease") || cleaned.includes("cap lease") || cleaned.includes("lease")) {
      return "LEASED";
    }

    if (/\b(own|owned|owning)\b/.test(cleaned)) {
      return "OWNED";
    }

    return undefined;
  };

  const toOptionalString = (value: any): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }
    return String(value);
  };

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setErrors([]);
    setWarnings([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        // cellDates: true makes date-formatted cells parse as JS Date objects instead of
        // raw Excel serial numbers (e.g. 45001) — without it, a serial can get misread
        // as a bare year or millisecond timestamp by new Date() (see formatDate below).
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(worksheet);

        // Normalize the data by mapping column names
        const normalizedData: EquipmentRow[] = rawData.map((row: any) => {
          const normalizedRow: any = {};
          
          Object.keys(row).forEach(originalKey => {
            const normalizedKey = normalizeColumnName(originalKey);
            const mappedKey = columnMapping[normalizedKey];
            if (mappedKey) {
              // Handle specific field types
              if (mappedKey === "Active") {
                normalizedRow[mappedKey] = parseBoolean(row[originalKey]);
              } else if (mappedKey === "Type") {
                const normalizedType = normalizeEquipmentType(row[originalKey]);
                normalizedRow[mappedKey] = normalizedType ?? row[originalKey];
              } else if (mappedKey === "Transmission") {
                const normalizedTrans = normalizeTransmissionType(row[originalKey]);
                normalizedRow[mappedKey] = normalizedTrans ?? row[originalKey];
              } else if (mappedKey === "Ownership Type") {
                const normalizedOwnership = normalizeOwnershipType(row[originalKey]);
                normalizedRow[mappedKey] = normalizedOwnership ?? row[originalKey];
              } else if (mappedKey === "License Plate") {
                const rawPlate = row[originalKey];
                const plateString = rawPlate === undefined || rawPlate === null ? "" : String(rawPlate);
                normalizedRow[mappedKey] = plateString.trim().length === 0 ? " " : plateString;
              } else if (["Manufactured Year", "List Price New", "Actual Cash Value", 
                         "Max Hauled Kg", "Trailer Axle Count", "Trailer Length"].includes(mappedKey)) {
                normalizedRow[mappedKey] = parseNumber(row[originalKey]);
              } else {
                normalizedRow[mappedKey] = row[originalKey];
              }
            }
          });
          
          return normalizedRow as EquipmentRow;
        });

        // Validate data structure
        const validationErrors: string[] = [];
        const validationWarnings: string[] = [];
        // Client required fields: VIN, Year, Make, Model, License Plate, Type
        const requiredColumns = ["Type", "VIN", "Manufactured Year", "Make", "Model"];
        
        if (normalizedData.length === 0) {
          validationErrors.push("File is empty or has no data rows");
        } else {
          // Auto-generate names if not provided
          normalizedData.forEach((row: any, index: number) => {
            if (!row.Name && row.Make && row.Model && row["Manufactured Year"]) {
              // Generate name from Make/Model/Year
              row.Name = `${row.Make} ${row.Model} ${row["Manufactured Year"]}`;
            } else if (!row.Name && row["License Plate"]) {
              // Fallback to license plate if no make/model
              row.Name = `Vehicle ${row["License Plate"]}`;
            } else if (!row.Name) {
              // Last resort - use row number
              row.Name = `Vehicle Row ${index + 2}`;
            }
          });

          // Check for duplicate names after generation
          const names = new Set<string>();
          normalizedData.forEach((row: EquipmentRow, index: number) => {
            const name = row.Name.trim();
            if (names.has(name)) {
              // Auto-append number to make unique
              let counter = 2;
              let uniqueName = `${name} (${counter})`;
              while (names.has(uniqueName)) {
                counter++;
                uniqueName = `${name} (${counter})`;
              }
              row.Name = uniqueName;
              names.add(uniqueName);
            } else {
              names.add(name);
            }

            // Validate required fields for each row
            if (!row.Type) {
              validationErrors.push(`Missing equipment type in row ${index + 2}`);
            } else {
              const normalizedType = normalizeEquipmentType(row.Type);
              if (!normalizedType) {
                validationErrors.push(`Invalid equipment type "${row.Type}" in row ${index + 2}. Valid types: ${equipmentTypes.join(", ")}`);
              } else {
                // Update the row with the normalized type
                row.Type = normalizedType;
              }
            }
            
            if (!row.VIN) {
              validationErrors.push(`Missing VIN in row ${index + 2}`);
            }
            
            if (!row["Manufactured Year"]) {
              validationErrors.push(`Missing Manufactured Year in row ${index + 2}`);
            }
            
            if (!row.Make) {
              validationErrors.push(`Missing Make in row ${index + 2}`);
            }
            
            if (!row.Model) {
              validationErrors.push(`Missing Model in row ${index + 2}`);
            }
            
            // Warn if license plate is blank
            const plateValue = row["License Plate"];
            if (plateValue === undefined || plateValue === null || String(plateValue).trim().length === 0 || plateValue === " ") {
              validationWarnings.push(`License Plate is blank in row ${index + 2}`);
            }

            // Validate transmission type if provided
            if (row.Transmission) {
              const normalizedTransmission = normalizeTransmissionType(row.Transmission);
              if (!normalizedTransmission) {
                validationErrors.push(`Invalid transmission type "${row.Transmission}" in row ${index + 2}. Valid types: AUTOMATIC, MANUAL (also accepts: CVT, Semi-Automatic, Auto, etc.)`);
              } else {
                // Update the row with the normalized transmission
                row.Transmission = normalizedTransmission;
              }
            }

            // Validate ownership type if provided
            if (row["Ownership Type"]) {
              const normalizedOwnership = normalizeOwnershipType(row["Ownership Type"]);
              if (!normalizedOwnership) {
                validationErrors.push(`Invalid ownership type "${row["Ownership Type"]}" in row ${index + 2}. Valid types: ${ownershipTypes.join(", ")}`);
              } else {
                row["Ownership Type"] = normalizedOwnership;
              }
            }
          });
        }

        if (validationErrors.length > 0) {
          setErrors(validationErrors);
        } else {
          setWarnings(validationWarnings);
          setEquipmentData(normalizedData);
          setActiveStep(1);
        }
      } catch (error) {
        setErrors(["Failed to parse Excel file. Please ensure it's a valid .xlsx file."]);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleCreateEquipment = async () => {
    setLoading(true);

    try {
      const bulkData = {
        equipment: equipmentData.map(equipment => ({
          name: equipment.Name.trim(),
          type: equipment.Type, // Already normalized during validation
          active: equipment.Active !== undefined ? equipment.Active : true,
          vin: equipment.VIN,
          licensePlate: equipment["License Plate"],
          jurisdiction: equipment.Jurisdiction,
          manufacturedYear: equipment["Manufactured Year"],
          make: equipment.Make,
          model: equipment.Model,
          mode: equipment.Mode,
          transmission: equipment.Transmission, // Already normalized during validation
          ownershipType: equipment["Ownership Type"], // Already normalized during parsing
          acquireDate: toOptionalString(equipment["Acquire Date"]),
          disposeDate: toOptionalString(equipment["Dispose Date"]),
          listPriceNew: equipment["List Price New"],
          actualCashValue: equipment["Actual Cash Value"],
          maxHauledKg: equipment["Max Hauled Kg"],
          trailerAxleCount: equipment["Trailer Axle Count"],
          trailerLength: equipment["Trailer Length"],
          trailerType: equipment["Trailer Type"],
          cvor: equipment.CVOR,
          usDotNumber: toOptionalString(equipment["US DOT Number"]),
          registrationExpiry: toOptionalString(equipment["Registration Expiry"]),
          safetyExpiry: toOptionalString(equipment["Safety Expiry"]),
          insuranceExpiry: toOptionalString(equipment["Insurance Expiry"]),
          notes: equipment.Notes,
        })),
      };

      // Use direct fetch to call the backend API
      const accessToken = localStorage.getItem('accessToken');
      const apiUrl = baseUrl || import.meta.env.VITE_API_URL;
      if (!apiUrl) {
        throw new Error("API base URL is not configured (VITE_SIMPLE_REST_URL or VITE_API_URL).");
      }

      // Get tenant code - prefer context, then localStorage
      let tenantCode: string | null = null;
      const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
      // First try to get from tenant context (most reliable)
      if (currentTenant && currentTenant.code) {
        tenantCode = currentTenant.code;
      } else {
        // Fallback to localStorage (same as customDataProvider)
        const currentTenantJson = localStorage.getItem('currentTenant');
        if (currentTenantJson) {
          try {
            const storedTenant = JSON.parse(currentTenantJson);
            if (storedTenant && storedTenant.code) {
              tenantCode = storedTenant.code;
            }
          } catch (e) {
            console.error('Error parsing current tenant from localStorage:', e);
          }
        }
      }

      // Validate tenant code is available
      if (!tenantCode) {
        const errorMsg = 'Tenant code is required but not found. Please ensure you are logged in and a tenant is selected.';
        notify(errorMsg, { type: 'error', autoHideDuration: 10000 });
        console.error('Tenant code missing:', {
          tenantsEnabled,
          currentTenantFromContext: currentTenant,
          currentTenantFromStorage: localStorage.getItem('currentTenant'),
        });
        setLoading(false);
        return;
      }

      console.log('Bulk equipment upload - Tenant code:', tenantCode);
      
      const response = await fetch(`${apiUrl}/equipment/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Tenant-Code': tenantCode,
        },
        body: JSON.stringify(bulkData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const results = await response.json();

      console.log("Bulk equipment creation response:", results);

      if (results && results.successful && results.successful.length > 0) {
        notify(`Successfully created ${results.successful.length} equipment items`, { type: "success" });
      }
      
      if (results && results.failed && results.failed.length > 0) {
        notify(`Failed to create ${results.failed.length} equipment items. Check console for details.`, { type: "warning" });
        console.error("Equipment creation failures:", results.failed);
      }

      if (!results || (!results.successful && !results.failed)) {
        notify("Bulk equipment creation completed, but response format was unexpected", { type: "warning" });
        console.log("Unexpected response format:", response);
      }

      // Refresh the list
      refresh();
      onClose();
      handleReset();
    } catch (error: any) {
      const errorMessage = error?.body?.message || error?.message || "Unknown error";
      notify(`Bulk equipment creation failed: ${errorMessage}`, { type: "error" });
      console.error("Bulk equipment creation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setEquipmentData([]);
    setErrors([]);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      handleReset();
    }
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        // Example 1: All fields populated (Name will be auto-generated if omitted)
        "Type": "TRUCK",
        "VIN": "1HGBH41JXMN109186",
        "Manufactured Year": 2021,
        "Make": "Freightliner",
        "Model": "Cascadia",
        "License Plate": "ABC-1234",
        "Name": "Truck 001",  // Optional - will be "Freightliner Cascadia 2021" if omitted
        "Active": "Yes",
        "Jurisdiction": "Ontario",
        "Mode": "Highway",
        "Transmission": "AUTOMATIC",
        "Ownership Type": "OWNED",
        "Acquire Date": "2021-06-15",
        "List Price New": 150000,
        "Actual Cash Value": 120000,
        "Max Hauled Kg": 20000,
        "CVOR": "123456",
        "US DOT Number": "789012",
        "Registration Expiry": "2025-06-15",
        "Safety Expiry": "2025-06-15",
        "Insurance Expiry": "2025-12-31",
        "Notes": "Primary long-haul truck"
      },
      {
        // Example 2: Only required fields (Name auto-generated)
        "Type": "TRAILER",
        "VIN": "2T1BR32E66C545490",
        "Manufactured Year": 2020,
        "Make": "Great Dane",
        "Model": "Everest",
        "License Plate": "TRL-5678"
      },
      {
        // Example 3: Alternative column naming also works
        "type": "VAN",
        "vin": "3VWDP7AJ7DM356789",
        "manufactured_year": 2019,
        "make": "Mercedes-Benz",
        "model": "Sprinter",
        "license_plate": "VAN-9876",
        "jurisdiction": "Quebec",
        "ownership_type": "LEASED"
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Equipment");
    XLSX.writeFile(workbook, "equipment_upload_template.xlsx");
  };

  const formatDate = (dateValue?: string | number | Date) => {
    if (!dateValue) return "-";
    try {
      // A raw number here is an Excel serial date that cellDates didn't convert
      // (e.g. a date typed into a non-date-formatted cell). new Date(number) treats
      // it as a millisecond timestamp, so it must be converted explicitly instead
      // of handed to new Date() directly.
      const EXCEL_EPOCH_OFFSET_DAYS = 25569;
      const parsed =
        dateValue instanceof Date
          ? dateValue
          : typeof dateValue === "number"
          ? new Date(Math.round((dateValue - EXCEL_EPOCH_OFFSET_DAYS) * 86400 * 1000))
          : new Date(dateValue);

      const year = parsed.getUTCFullYear();
      if (isNaN(parsed.getTime()) || year < 1900 || year > 2200) return "-";
      // Spreadsheet dates (via cellDates, the serial conversion above, or an
      // ISO date-only string) are all anchored to UTC midnight with no timezone
      // of their own. Rendering in the browser's local timezone can shift the
      // displayed day back by one west of UTC — force UTC so the calendar day
      // shown always matches the one in the spreadsheet.
      return parsed.toLocaleDateString(undefined, { timeZone: "UTC" });
    } catch {
      return String(dateValue);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Upload an Excel file (.xlsx) with equipment data. Column names are flexible - spaces, underscores, dashes, and capitalization don't matter.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Photos cannot be uploaded via Excel. Please add photos individually after import.
              </Typography>
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Required columns:</strong>
            </Typography>
            <ul>
              <li><strong>Type</strong> (TRUCK, TRAILER, CAR, VAN, TRACTOR, etc.)</li>
              <li><strong>VIN</strong> (Vehicle Identification Number)</li>
              <li><strong>Manufactured Year</strong> (Year of manufacture)</li>
              <li><strong>Make</strong> (Vehicle manufacturer)</li>
              <li><strong>Model</strong> (Vehicle model)</li>
            </ul>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Optional columns:</strong>
            </Typography>
            <ul>
              <li><strong>Name</strong> (If not provided, will be auto-generated from Make/Model/Year)</li>
              <li><strong>License Plate</strong> (Vehicle registration plate, can be blank)</li>
              <li>Active, Jurisdiction, Mode, Transmission, Ownership Type, CVOR, DOT Number</li>
              <li>Various dates (Acquire Date, Registration Expiry, Safety Expiry, Insurance Expiry)</li>
              <li>Financial info (List Price, Cash Value), Trailer specs, Notes</li>
            </ul>
            
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2">Please fix the following errors:</Typography>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2">Warnings:</Typography>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                sx={{ mb: 2, mr: 2 }}
                size="small"
              >
                Download Template
              </Button>
              <input
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                id="upload-file"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-file">
                <Button variant="outlined" component="span" fullWidth size="large">
                  Choose Excel File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review {equipmentData.length} equipment items to be created:
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>License Plate</TableCell>
                    <TableCell>Make/Model</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Ownership</TableCell>
                    <TableCell>Registration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipmentData.map((equipment, index) => (
                    <TableRow key={index}>
                      <TableCell>{equipment.Name}</TableCell>
                      <TableCell>
                        <Chip label={equipment.Type} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        {equipment.Active !== false ? (
                          <Chip label="Active" size="small" color="success" />
                        ) : (
                          <Chip label="Inactive" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{equipment["License Plate"] || "-"}</TableCell>
                      <TableCell>
                        {equipment.Make && equipment.Model 
                          ? `${equipment.Make} ${equipment.Model}`
                          : equipment.Make || equipment.Model || "-"}
                      </TableCell>
                      <TableCell>{equipment["Manufactured Year"] || "-"}</TableCell>
                      <TableCell>
                        {equipment["Ownership Type"] && (
                          <Chip label={equipment["Ownership Type"]} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(equipment["Registration Expiry"])}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to create {equipmentData.length} equipment items. This action cannot be undone.
            </Alert>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Summary of equipment to be created:
            </Typography>
            <ul>
              {equipmentTypes.map(type => {
                const count = equipmentData.filter(e => e.Type?.toUpperCase() === type).length;
                return count > 0 ? (
                  <li key={type}>
                    <strong>{type}:</strong> {count} item{count !== 1 ? 's' : ''}
                  </li>
                ) : null;
              })}
            </ul>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Click "Create Equipment" to proceed with bulk equipment creation.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Bulk Equipment Upload
        {loading && <LinearProgress sx={{ mt: 1 }} />}
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button
            onClick={() => setActiveStep(activeStep - 1)}
            disabled={loading}
          >
            Back
          </Button>
        )}
        
        {activeStep === 1 && (
          <Button
            onClick={() => setActiveStep(2)}
            variant="contained"
            disabled={equipmentData.length === 0}
          >
            Next
          </Button>
        )}
        
        {activeStep === 2 && (
          <Button
            onClick={handleCreateEquipment}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Create Equipment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};